import { useState } from "react";
import { useQuery, useMutation, useApolloClient } from "@apollo/client/react";
import { GET_REQUIREMENTS, GET_TESTS, GET_STATS, CREATE_TEST, LINK_TEST_TO_REQUIREMENT } from "@/graphql/queries";
import { mapToSnakeCase } from "@/graphql/mappers";
import { useApp } from "@/context/AppContext";
import type { Requirement } from "@/types";

export function CreateTest() {
  const { createTestRequirementId, navigateTo, setCurrentView, activeUser, activeProject } = useApp();
  const client = useApolloClient();

  const [formData, setFormData] = useState({
    id: "",
    title: "",
    requirementId: createTestRequirementId || "",
    description: "",
    type: "",
    suite: "",
    test_status: "",
  });
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const { data: reqsRaw } = useQuery<any>(GET_REQUIREMENTS);
  const requirements: Requirement[] = reqsRaw ? mapToSnakeCase(reqsRaw.requirements) : [];

  // Only leaf requirements (implementation level) can have tests
  const leafRequirements = requirements
    .filter((r: Requirement) => r.level === "implementation")
    .sort((a: Requirement, b: Requirement) => a.id.localeCompare(b.id));

  const [createTestMutation] = useMutation<any>(CREATE_TEST);
  const [linkTestMutation] = useMutation<any>(LINK_TEST_TO_REQUIREMENT);

  const canSubmit = formData.id.trim() && formData.title.trim() && formData.requirementId && !submitting;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit || !activeUser) return;
    setError(null);
    setSubmitting(true);

    try {
      // Create the test
      await createTestMutation({
        variables: {
          input: {
            id: formData.id.trim(),
            title: formData.title.trim(),
            description: formData.description.trim() || undefined,
            type: formData.type || undefined,
            suite: formData.suite || undefined,
            testStatus: formData.test_status || undefined,
          },
          projectId: activeProject,
          userId: activeUser.id,
        },
      });

      // Link it to the parent requirement
      await linkTestMutation({
        variables: {
          testId: formData.id.trim(),
          requirementId: formData.requirementId,
          projectId: activeProject,
          userId: activeUser.id,
        },
      });

      // Refetch and navigate
      await client.refetchQueries({ include: [GET_TESTS, GET_REQUIREMENTS, GET_STATS] });
      navigateTo("tests", formData.id.trim());
    } catch (err: any) {
      setError(err.message || "Failed to create test");
      setSubmitting(false);
    }
  }

  function handleCancel() {
    setCurrentView("tests");
  }

  function setField(field: string, value: string) {
    setFormData(prev => ({ ...prev, [field]: value }));
  }

  const selectedReq = formData.requirementId
    ? requirements.find((r: Requirement) => r.id === formData.requirementId)
    : null;

  return (
    <div className="flex flex-col h-full overflow-auto">
      <div className="max-w-2xl mx-auto w-full px-7 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            className="text-xs text-text2 hover:text-accent transition-colors bg-transparent border-none cursor-pointer mb-3 flex items-center gap-1"
            onClick={handleCancel}
          >
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-3 h-3">
              <path d="M10 3L5 8l5 5" />
            </svg>
            Back to Tests
          </button>
          <h1 className="text-xl font-semibold text-text leading-tight">New Test</h1>
          <p className="text-xs text-text2 mt-1">
            {createTestRequirementId
              ? <>Linking to requirement <span className="text-accent font-medium">{createTestRequirementId}</span></>
              : "Create a new test linked to a requirement"
            }
          </p>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-5 p-3 bg-red/10 border border-red/30 rounded-default text-red text-xs">
            {error}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          {/* ID */}
          <FormField label="Test ID" required>
            <input
              type="text"
              value={formData.id}
              onChange={e => setField("id", e.target.value)}
              placeholder="e.g. TEST-FW-001"
              autoFocus
              className="w-full px-3 py-2 bg-bg border border-border rounded-default text-text text-sm outline-none transition-colors focus:border-accent placeholder:text-text2"
            />
          </FormField>

          {/* Title */}
          <FormField label="Title" required>
            <input
              type="text"
              value={formData.title}
              onChange={e => setField("title", e.target.value)}
              placeholder="e.g. Overcurrent protection test"
              className="w-full px-3 py-2 bg-bg border border-border rounded-default text-text text-sm outline-none transition-colors focus:border-accent placeholder:text-text2"
            />
          </FormField>

          {/* Parent Requirement (required) */}
          <FormField label="Parent Requirement" required hint="Every test must be linked to a requirement">
            <div className="flex items-center gap-2">
              <select
                value={formData.requirementId}
                onChange={e => setField("requirementId", e.target.value)}
                className="flex-1 px-3 py-2 bg-bg border border-border rounded-default text-text text-sm outline-none transition-colors focus:border-accent"
              >
                <option value="">Select a requirement...</option>
                {leafRequirements.map((r: Requirement) => (
                  <option key={r.id} value={r.id}>
                    {r.id} — {r.description || r.title || "No description"}
                  </option>
                ))}
              </select>
              {formData.requirementId && (
                <button
                  type="button"
                  className="text-text2 hover:text-text text-xs bg-transparent border-none cursor-pointer"
                  onClick={() => setField("requirementId", "")}
                >
                  Clear
                </button>
              )}
            </div>
            {selectedReq && (
              <div className="mt-1.5 text-[11px] text-text2">
                <span className="text-text">{selectedReq.level}</span> · {selectedReq.description || selectedReq.title || "No description"}
              </div>
            )}
          </FormField>

          {/* Description */}
          <FormField label="Description">
            <textarea
              value={formData.description}
              onChange={e => setField("description", e.target.value)}
              placeholder="Describe what this test verifies..."
              rows={4}
              className="w-full px-3 py-2 bg-bg border border-border rounded-default text-text text-sm outline-none transition-colors resize-y min-h-[80px] focus:border-accent placeholder:text-text2"
            />
          </FormField>

          {/* Type + Suite row */}
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Type">
              <select
                value={formData.type}
                onChange={e => setField("type", e.target.value)}
                className="w-full px-3 py-2 bg-bg border border-border rounded-default text-text text-sm outline-none transition-colors focus:border-accent"
              >
                <option value="">None</option>
                <option value="checklist">Checklist</option>
                <option value="jira-tracked">Jira-tracked</option>
                <option value="pipeline">Pipeline</option>
              </select>
            </FormField>

            <FormField label="Suite">
              <input
                type="text"
                value={formData.suite}
                onChange={e => setField("suite", e.target.value)}
                placeholder="e.g. smoke, regression"
                className="w-full px-3 py-2 bg-bg border border-border rounded-default text-text text-sm outline-none transition-colors focus:border-accent placeholder:text-text2"
              />
            </FormField>
          </div>

          {/* Test Status (for checklist/pipeline) */}
          {formData.type && formData.type !== "jira-tracked" && (
            <FormField label="Test Status">
              <select
                value={formData.test_status || "unknown"}
                onChange={e => setField("test_status", e.target.value)}
                className="w-full px-3 py-2 bg-bg border border-border rounded-default text-text text-sm outline-none transition-colors focus:border-accent"
              >
                <option value="done">Done</option>
                <option value="not_done">Not done</option>
                <option value="pending">Pending</option>
                <option value="unknown">Unknown</option>
              </select>
            </FormField>
          )}


          {/* Actions */}
          <div className="flex items-center gap-3 pt-3 border-t border-border">
            <button
              type="submit"
              disabled={!canSubmit}
              className="px-5 py-2 bg-accent text-white border border-accent rounded-default text-sm font-medium cursor-pointer transition-colors hover:bg-accent2 hover:border-accent2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? "Creating..." : "Create Test"}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="px-5 py-2 bg-transparent text-text2 border border-border rounded-default text-sm cursor-pointer transition-colors hover:text-text hover:border-text2"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
function FormField({ label, required, hint, children }: { label: string; required?: boolean; hint?: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-baseline gap-1.5">
        <label className="text-xs text-text font-medium">
          {label}
          {required && <span className="text-red ml-0.5">*</span>}
        </label>
        {hint && <span className="text-[10px] text-text2">{hint}</span>}
      </div>
      {children}
    </div>
  );
}
